import { FC, useContext } from 'react';


import { useContent } from '@eneco/cms';
import RichText from '@eneco/components/RichText/RichText';
import { TrackedDialog } from '@eneco/components/TrackedDialog/TrackedDialog';
import { TrackedNotificationBox } from '@eneco/components/TrackedNotificationBox/TrackedNotificationBox';
import { typedYupResolver } from '@eneco/hooks/form';
import { Button, Form, Heading, InputEmail, Stack, Text, NavLink, TextLink, InputPassword } from '@eneco/sparky';
import { ChevronLeftIcon, InfoIcon } from '@eneco/sparky/icons';
import { TrackViewComponent } from '@eneco/tracking';
import { SubmitHandler, useForm } from 'react-hook-form';

import { RegistrationAccountContent } from '../registrationFlow.types';
import { registerPersonSchema } from './registerPersonSchema';
import { Action, State } from '../registrationFlow.constants';
import { RegistrationMachine } from '../registrationFlow.machine';

const resolver = typedYupResolver<FormValues>(registerPersonSchema);

type FormValues = {
  emailAddress: string;
  password: string;
};

const ERROR_KEYS = {
  CONFLICT: 'conflictErrorNotification',
  TECHNICAL: 'technicalErrorNotificationField',
} as const;

export const RegisterPerson: FC = () => {
  const content = useContent<RegistrationAccountContent>();
  const { send, state } = useContext(RegistrationMachine);
  const isSettingNewCredentials = state.value === State.SETTING_NEW_CREDENTIALS;
  const { hasError, errorStatusCode } = state.context;
  const {
    formState: { errors },
    handleSubmit,
    register,
  } = useForm<FormValues>({
    defaultValues: {
      emailAddress: state.context.emailAddress,
    },
    resolver,
  });
  const handleBack = () => send({ type: Action.BACK });

  const submitForm: SubmitHandler<FormValues> = ({ emailAddress, password }) => {
    send({ type: Action.CUSTOMER_SET_CREDENTIALS, emailAddress, password });
  };

  const formErrorMessages: { [key: string]: string } = {
    emailRequired: content.RegisterPerson_emailFormField?.requiredMessage ?? '',
    emailInvalid: content.RegisterPerson_emailFormField?.validationMessage ?? '',
    passwordRequired: content.RegisterPerson_passwordFormField?.requiredMessage ?? '',
    passwordInvalid: content.RegisterPerson_passwordFormField?.validationMessage ?? '',
  };

  const errorKey = errorStatusCode === 409 ? ERROR_KEYS.CONFLICT : ERROR_KEYS.TECHNICAL;

  return (
    <Form onSubmit={handleSubmit(submitForm)}>
      <Stack gap="6">
        <NavLink variant="secondary" onClick={handleBack} leftIcon={<ChevronLeftIcon />}>
          {content.RegisterPerson_backLinkText}
        </NavLink>
        <Heading as="h1" size="XS">
          {content.RegisterPerson_title}
        </Heading>
        {hasError && (
          <TrackedNotificationBox
            label={content[`RegisterPerson_${errorKey}`]?.title}
            title={content[`RegisterPerson_${errorKey}`]?.title}
            isAlert
            text={<RichText html={content[`RegisterPerson_${errorKey}`]?.content} />}
            variant="error"
          />
        )}
        <Stack gap="3">
          <Text>{content.RegisterPerson_introductionText}</Text>
          <TrackedDialog
            trigger={
              <TextLink rightIcon={<InfoIcon size="small" />}>
                {content.RegisterPerson_modalDialog?.triggerText}
              </TextLink>
            }
            title={content.RegisterPerson_modalDialog?.title}>
            <TrackViewComponent type="dialog" label={content.RegisterPerson_modalDialog?.title ?? ''}>
              <Stack gap="4">
                <RichText html={content.RegisterPerson_modalDialog?.content} />
              </Stack>
            </TrackViewComponent>
          </TrackedDialog>
        </Stack>
        <InputEmail
          {...register('emailAddress')}
          label={content.RegisterPerson_emailFormField?.label ?? ''}
          autoComplete="email"
          error={errors?.emailAddress?.message && formErrorMessages[errors.emailAddress.message]}
          isReadOnly={isSettingNewCredentials}
          placeholder={content.RegisterPerson_emailFormField?.placeholder}
          defaultValue={state.context.emailAddress}
        />
        <InputPassword
          {...register('password')}
          label={content.RegisterPerson_passwordFormField?.label ?? ''}
          autoComplete="new-password"
          hint={content.RegisterPerson_passwordFormField?.hint}
          error={errors?.password?.message && formErrorMessages[errors.password.message]}
          isReadOnly={isSettingNewCredentials}
        />
        <Button type="submit" isLoading={isSettingNewCredentials}>
          {content.RegisterPerson_continueButtonText}
        </Button>
      </Stack>
    </Form>
  );
};
